/**
 * FloatingActionButton (FAB) Component
 *
 * Mobile-only floating action button positioned at bottom-right.
 * Used to trigger the BottomActionSheet on mobile devices.
 *
 * Features:
 * - Brand-colored styling (lime accent #D2FA52)
 * - Fixed position bottom-right with safe-area padding
 * - 56px touch target (exceeds 44px WCAG AA minimum)
 * - Hidden on tablet and desktop (md:hidden)
 * - Accessible with aria-label
 *
 * @see Story 5.3: Bottom Action Sheet
 */

import type React from 'react';

// ============================================================================
// TYPES
// ============================================================================

export interface FloatingActionButtonProps {
  /** Click handler */
  onClick: () => void;
  /** Optional custom icon (defaults to menu/dots icon) */
  icon?: React.ReactNode;
  /** Accessible label for the button */
  ariaLabel?: string;
}

// ============================================================================
// DEFAULT ICON
// ============================================================================

function DefaultMenuIcon(): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Three horizontal dots (more menu) */}
      <circle cx="12" cy="12" r="1" fill="currentColor" />
      <circle cx="12" cy="5" r="1" fill="currentColor" />
      <circle cx="12" cy="19" r="1" fill="currentColor" />
    </svg>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export function FloatingActionButton({
  onClick,
  icon,
  ariaLabel = 'Abrir menu de acoes',
}: FloatingActionButtonProps): JSX.Element {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      className="
        fixed bottom-6 right-6 z-50
        flex items-center justify-center
        w-14 h-14 rounded-full
        bg-brand-primary text-brand-dark
        shadow-lg hover:shadow-xl
        transition-all duration-200
        hover:scale-105 active:scale-95
        focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-brand-dark
        md:hidden
        motion-reduce:transition-none motion-reduce:hover:scale-100 motion-reduce:active:scale-100
      "
      style={{
        // Ensure safe area on iOS
        marginBottom: 'env(safe-area-inset-bottom, 0)',
      }}
    >
      {icon || <DefaultMenuIcon />}
    </button>
  );
}

export default FloatingActionButton;
